/**
 * useCalendarItems — Source unique des items du calendrier, réutilisable.
 *
 * Agrège toutes les sources (événements, tâches, publications, réunions,
 * campagnes, demandes, Metricool, tournois, événements comm) via le moteur
 * partagé lib/calendarItems, puis filtre par droits (admin = tout ; sinon
 * ses clubs + le national). Utilisé par le dashboard ; le calendrier garde
 * sa propre logique de filtres d'affichage mais partage le même moteur.
 *
 * @param {object} currentUser — profil ep:users (admin, clubs…)
 * @param {string|number} appId — identifiant du compte connecté
 * @returns {{ items: Array, loading: boolean }}
 */
import { useEffect, useMemo, useState } from "react";
import {
  useCalendarEvents, useTasks, usePublications,
  useMeetings, useMetricool, useClubs,
  useCampagnes, useRequests, useUsers,
  useCommEvents,
} from "./useV1Data";
import { buildAllItems, filterVisibleItems } from "../lib/calendarItems";

export function useCalendarItems(currentUser, appId) {
  const { events,       loading: lEvents } = useCalendarEvents();
  const { tasks,        loading: lTasks }  = useTasks();
  const { publications, loading: lPub }    = usePublications();
  const { meetings,     loading: lMeet }   = useMeetings();
  const { metricool }                       = useMetricool();
  const { campagnes }                       = useCampagnes();
  const { requests,     loading: lReq }    = useRequests();
  const { clubs,        loading: lClubs }  = useClubs();
  const { users,        loading: lUsers }  = useUsers();
  const { commEvents }                      = useCommEvents();

  // Tournois (tous clubs) — API externe, lecture seule. Échec silencieux.
  const [tournaments, setTournaments] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch("/api/events/tournaments", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setTournaments((j && j.tournaments) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => {
    const all = buildAllItems({
      tournaments, events, commEvents, publications, meetings,
      tasks, campagnes, requests, metricool, clubs, users,
    });
    return filterVisibleItems(all, currentUser, appId);
  }, [tournaments, events, commEvents, publications, meetings, tasks, campagnes, requests, metricool, clubs, users, currentUser, appId]);

  const loading = lEvents || lTasks || lPub || lMeet || lReq || lClubs || lUsers;

  return { items, loading };
}
